import React, { useRef, useEffect, useMemo } from 'react';
import { cn } from "@/lib/utils";
import { detectHashtags } from '@/utils/hashtag-utils';

interface HashtagTextInputProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  value?: string;
  onChange?: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onCursorPositionChange?: () => void;
  textareaRef: React.RefObject<HTMLTextAreaElement>;
  borderColor?: string;
}

const HashtagTextInput = ({ 
  value = '',
  onChange,
  onCursorPositionChange,
  textareaRef,
  borderColor = "border-white/10",
  className,
  ...props 
}: HashtagTextInputProps) => {
  const mirrorRef = useRef<HTMLDivElement>(null);
  const hashtags = useMemo(() => detectHashtags(value), [value]);

  // Auto-resize functionality
  const autoResize = () => {
    if (!textareaRef.current) return;
    textareaRef.current.style.height = 'auto';
    textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
  };

  useEffect(() => {
    autoResize();
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    onChange?.(e);
    autoResize();
  };

  const handleCursorUpdate = () => {
    onCursorPositionChange?.();
  };

  // Create text with transparent hashtags for textarea
  const createTextareaContent = () => {
    if (hashtags.length === 0) {
      return value;
    }

    let result = '';
    let lastIndex = 0;

    hashtags.forEach((hashtag) => {
      // Add text before hashtag
      result += value.slice(lastIndex, hashtag.start);
      // Add transparent hashtag placeholder
      result += '<span style="color: transparent;">' + hashtag.text + '</span>';
      lastIndex = hashtag.end;
    });

    // Add remaining text
    result += value.slice(lastIndex);
    return result;
  };

  // Create only hashtag highlights for overlay
  const createHashtagOverlay = () => {
    if (hashtags.length === 0) {
      return '';
    }

    let result = '';
    let lastIndex = 0;

    hashtags.forEach((hashtag) => {
      // Add spaces/invisible text before hashtag to maintain positioning
      const beforeText = value.slice(lastIndex, hashtag.start);
      result += '<span style="color: transparent;">' + beforeText + '</span>';
      // Add hashtag with gradient
      result += `<span class="hashtag-highlight">${hashtag.text}</span>`;
      lastIndex = hashtag.end;
    });

    return result;
  };

  return (
    <div className="relative">
      {/* Actual textarea */}
      <textarea
        ref={textareaRef}
        value={value}
        onChange={handleChange}
        onKeyUp={handleCursorUpdate}
        onClick={handleCursorUpdate}
        className={cn(
          "relative w-full p-4 rounded-2xl bg-white/5 backdrop-blur-sm border transition-all duration-200",
          "focus-visible:outline-none focus-visible:border-primary/60 focus-visible:shadow-[0_0_0_3px_rgb(139,69,19,0.1)] resize-none",
          "text-white/90 placeholder:text-gray-400",
          borderColor,
          className
        )}
        style={{ 
          caretColor: 'white', 
          minHeight: '80px',
          background: 'transparent'
        }}
        {...props}
      />

      {/* Hashtag highlight overlay */}
      <div
        ref={mirrorRef}
        className={cn(
          "absolute inset-0 pointer-events-none whitespace-pre-wrap break-words p-4 text-sm z-10",
          "overflow-hidden",
          className
        )}
        style={{
          lineHeight: 'inherit',
          fontFamily: 'inherit',
          fontSize: 'inherit',
          letterSpacing: 'inherit',
          wordSpacing: 'inherit',
          minHeight: '80px',
        }}
        dangerouslySetInnerHTML={{
          __html: createHashtagOverlay().replace(/\n/g, '<br/>')
        }}
      />

      {/* CSS for hashtag highlighting */}
      <style dangerouslySetInnerHTML={{
        __html: `
          .hashtag-highlight {
            background: linear-gradient(90deg, hsl(var(--primary)), hsl(var(--accent)), hsl(var(--primary)));
            background-clip: text;
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            font-weight: 500;
          }
        `
      }} />
    </div>
  );
};

export default HashtagTextInput;